import { Component } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { first, forkJoin, of } from 'rxjs';
import { CartService, GungModalService, ProductService, RemoveBlockedProductsStepComponent } from 'gung-standard';

// Product 46607-03 is a virtual/custom order line added by sales/admin users via the "+" button on the cart page
// (see CarLinerCheckoutAddRowModalComponent). It does not exist in the standard product catalog and is therefore
// flagged as "blocked" by the backend's products-blocked-flow-multi endpoint. This custom step ensures that
// product 46607-03 is always excluded from the blocked-products removal so that custom freight/fee rows survive checkout.
@Component({
  selector: 'car-liner-remove-blocked-products-step',
  template: '',
  standalone: false
})
export class CarLinerRemoveBlockedProductsStepComponent extends RemoveBlockedProductsStepComponent {
  // Product IDs that should never be auto-removed by the blocked-products check.
  // 46607-03 is the virtual product used for custom order lines (freight, fees, etc.).
  private readonly EXCLUDED_PRODUCT_IDS = new Set(['46607-03']);

  constructor(
    protected productService: ProductService,
    protected cartService: CartService,
    protected modalService: GungModalService,
    protected translateService: TranslateService
  ) {
    super(productService, cartService, modalService, translateService);
  }

  protected checkBlockedProducts(): void {
    this.onNextBtnClicked.pipe(first()).subscribe(() => {
      this.cartService
        .getCurrentCart()
        .pipe(first())
        .subscribe(currentCartRows => {
          const productIds = currentCartRows.map(cartRow => cartRow.productId);

          forkJoin({
            cartRows: of(currentCartRows),
            blockedProducts: this.productService.getBlockedProducts(productIds).pipe(first())
          }).subscribe(({ cartRows, blockedProducts }) => {
            const blockedProductsIds = new Set<string>();
            const rowsToRemove: any[] = [];
            for (const productId of Object.keys(blockedProducts)) {
              // Skip products that are intentionally virtual and not in the catalog
              if (this.EXCLUDED_PRODUCT_IDS.has(productId)) {
                continue;
              }
              if (blockedProducts[productId]) {
                const blockedCartRows = cartRows.filter(cr => cr.productId === productId);
                if (!!blockedCartRows) {
                  rowsToRemove.push(...blockedCartRows);
                  blockedProductsIds.add(productId);
                }
              }
            }
            this.cartService.bulkRemoveRows(rowsToRemove);

            if (blockedProductsIds.size > 0) {
              this.modalService
                .openConfirmYesNoModal(
                  this.translateService.instant('BLOCKED_PRODUCTS'),
                  this.translateService.instant('BLOCKED_PRODUCTS_MESSAGE', {
                    products: [...blockedProductsIds.values()].join(', ')
                  }),
                  null,
                  'OK',
                  null
                )
                .then(_ => {});
            }

            this.stepDone.emit(this.checkout);
          });
        });
    });
  }
}
